import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import "./Navbar.css";

const Navbar: React.FC = () => {
    const location = useLocation();

    // UI global
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isOverHero, setIsOverHero] = useState(true);

    // Solution submenu
    const [solOpen, setSolOpen] = useState(false);
    const solRef = useRef<HTMLLIElement>(null);
    const [solPos, setSolPos] = useState<{ left: number; top: number; minWidth: number }>({
        left: 0,
        top: 0,
        minWidth: 240,
    });
    // Control de timeout para evitar cierre inmediato al quitar el hover
    const solTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Desktop / mobile breakpoint
    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
    useEffect(() => {
        const onResize = () => {
            const width = window.innerWidth;
            setIsDesktop(width > 768);
        };
        // Asegurarse de que isDesktop se actualiza inmediatamente
        onResize();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const closeMenu = (src = "close") => setIsMenuOpen(false);
    const toggleMenu = () => setIsMenuOpen((p) => !p);

    // Lock body scroll cuando el drawer está abierto
    useEffect(() => {
        document.body.style.overflow = isMenuOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isMenuOpen]);

    // Compact + estado "sobre el hero"
    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY || 0;
            setIsScrolled(y > 10);
            setIsOverHero(y < 80);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Recolocar submenú (desktop) bajo el trigger
    const placeSol = useCallback(() => {
        const el = solRef.current;
        if (!el) return;

        // Obtener las coordenadas actuales del botón de "Solution"
        const r = el.getBoundingClientRect();

        // Cuando hacemos scroll, necesitamos usar las coordenadas fijas respecto a la ventana
        // ya que el navbar tiene position: fixed
        setSolPos({
            left: Math.round(r.left),
            top: Math.round(r.bottom + 12),
            minWidth: Math.max(240, Math.round(r.width)),
        });

        console.log('Calculated position:', {
            left: Math.round(r.left),
            top: Math.round(r.bottom + 12),
            scroll: window.scrollY,
            bounds: r
        });
    }, []);

    // Abrir/cerrar Solution
    const onToggleSolution = useCallback((e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation(); // Evita cierre inmediato por click global

        setSolOpen((prevOpen) => {
            const nextOpen = !prevOpen;

            // Si estamos abriendo, calcular posición
            if (nextOpen) {
                // Limpiar cualquier timeout pendiente
                if (solTimeoutRef.current) {
                    clearTimeout(solTimeoutRef.current);
                    solTimeoutRef.current = null;
                }

                // Calcular posición inmediatamente y luego después de un pequeño retraso
                placeSol();
                setTimeout(placeSol, 10);
            }

            return nextOpen;
        });
    }, [placeSol]);

    // Función para manejar el hover enter
    const handleMouseEnter = useCallback(() => {
        if (!isDesktop) return;

        // Cancelar cualquier timeout pendiente de cierre
        if (solTimeoutRef.current) {
            clearTimeout(solTimeoutRef.current);
            solTimeoutRef.current = null;
        }

        // Abrir el menú y calcular su posición
        setSolOpen(true);
        placeSol();
        setTimeout(placeSol, 10);
    }, [isDesktop, placeSol]);

    // Función para manejar el hover leave con delay
    const handleMouseLeave = useCallback(() => {
        if (!isDesktop) return;

        // Configurar un timeout para cerrar el menú con retraso
        // para dar tiempo a mover el cursor hacia el submenú
        solTimeoutRef.current = setTimeout(() => {
            // Verificamos si el cursor está sobre el portal del submenú
            const portalEl = document.getElementById("sol-submenu-portal");
            if (portalEl && isHovering(portalEl)) {
                // Si el cursor está sobre el submenú, no lo cerramos
                return;
            }

            // Si no está sobre el submenú, lo cerramos
            setSolOpen(false);
        }, 300); // 300ms de retraso para permitir mover el cursor al submenú
    }, [isDesktop]);

    // Función auxiliar para verificar si el cursor está sobre un elemento
    const isHovering = (element: HTMLElement): boolean => {
        const rect = element.getBoundingClientRect();
        // No podemos obtener la posición del cursor directamente, pero podemos
        // comparar con document.elementFromPoint en la posición aproximada
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const elementAtPoint = document.elementFromPoint(centerX, centerY);

        return element.contains(elementAtPoint);
    };

    // Cerrar Solution explícitamente
    const closeSolution = useCallback(() => {
        // Limpiar cualquier timeout pendiente
        if (solTimeoutRef.current) {
            clearTimeout(solTimeoutRef.current);
            solTimeoutRef.current = null;
        }
        setSolOpen(false);
    }, []);

    // Cierre "click afuera", ESC y reflow cuando está abierto
    useEffect(() => {
        if (!solOpen) return;

        console.log('Solution menu opened, isDesktop:', isDesktop);

        const onEsc = (e: KeyboardEvent) => e.key === "Escape" && closeSolution();

        const onClickOutside = (e: MouseEvent) => {
            const portalEl = document.getElementById("sol-submenu-portal");
            const target = e.target as Node;
            const clickedInsideTrigger = !!(solRef.current && solRef.current.contains(target));
            const clickedInsidePortal = !!(portalEl && portalEl.contains(target));

            if (!clickedInsideTrigger && !clickedInsidePortal) {
                closeSolution();
            }
        };

        const onReflow = () => placeSol();

        window.addEventListener("keydown", onEsc);
        window.addEventListener("click", onClickOutside);
        window.addEventListener("scroll", onReflow, { passive: true });
        window.addEventListener("resize", onReflow);

        // Ejecutar placeSol() para asegurar posicionamiento correcto
        placeSol();
        // Ejecutar de nuevo después de un pequeño retraso
        setTimeout(placeSol, 50);

        return () => {
            window.removeEventListener("keydown", onEsc);
            window.removeEventListener("click", onClickOutside);
            window.removeEventListener("scroll", onReflow);
            window.removeEventListener("resize", onReflow);
        };
    }, [solOpen, closeSolution, placeSol, isDesktop]);

    // Manejar hover en el portal del submenú
    useEffect(() => {
        if (!solOpen || !isDesktop) return;

        const portalEl = document.getElementById("sol-submenu-portal");
        if (!portalEl) return;

        // Cuando el mouse entra al submenú, cancelamos cualquier timeout de cierre
        const onPortalMouseEnter = () => {
            if (solTimeoutRef.current) {
                clearTimeout(solTimeoutRef.current);
                solTimeoutRef.current = null;
            }
        };

        // Cuando el mouse sale del submenú, empezamos un timer para cerrarlo
        const onPortalMouseLeave = () => {
            solTimeoutRef.current = setTimeout(() => {
                setSolOpen(false);
            }, 300);
        };

        portalEl.addEventListener("mouseenter", onPortalMouseEnter);
        portalEl.addEventListener("mouseleave", onPortalMouseLeave);

        return () => {
            portalEl.removeEventListener("mouseenter", onPortalMouseEnter);
            portalEl.removeEventListener("mouseleave", onPortalMouseLeave);
        };
    }, [solOpen, isDesktop]);

    // Cerrar drawer y dropdowns al navegar
    useEffect(() => {
        closeMenu("route-change");
        closeSolution();
    }, [location.pathname, closeSolution]);

    return (
        <nav className={`l-nav ${isScrolled ? "l-nav--scrolled" : "l-nav--top"} ${isOverHero ? "l-nav--over-hero" : ""}`}>
            <div className="l-nav__container">
                <div className="l-nav__shell glass border-gradient">
                    <div className="l-nav__inner">
                        {/* Brand */}
                        <div className="l-nav__brand">
                            <Link to="/" onClick={() => closeMenu("brand")} aria-label="Lummo Home" className="l-nav__brand-link">
                                <img src="/brand/__Logo_negro.png" alt="" className="l-nav__logo" />
                            </Link>
                        </div>

                        {/* Links / Drawer */}
                        <ul
                            id="mobile-menu"
                            className={`l-nav__links ${isMenuOpen ? "is-open" : ""}`}
                        >
                            {/* Botón cerrar (mobile) */}
                            <li>
                                <button type="button" className="l-nav__close" onClick={() => closeMenu("close-x")} aria-label="Close menu" />
                            </li>

                            {/* ======= Solution con submenú ======= */}
                            <li
                                ref={solRef}
                                className={`l-nav__item has-sub ${solOpen ? "is-open" : ""}`}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                <button
                                    type="button"
                                    className="l-nav__link-btn"
                                    aria-haspopup="true"
                                    aria-expanded={solOpen}
                                    onClick={onToggleSolution}
                                >
                                    Solution <span className="l-nav__chev">▾</span>
                                </button>

                                {/* Submenú INLINE para mobile (colapsable dentro del drawer) */}
                                <ul className="l-nav__submenu--inline" role="menu" aria-label="Solution">
                                    <li role="none">
                                        <Link role="menuitem" to="/solution/cro" onClick={() => { setSolOpen(false); closeMenu("sub-cro"); }}>
                                            CRO
                                        </Link>
                                    </li>
                                    <li role="none">
                                        <Link role="menuitem" to="/solution/sites" onClick={() => { setSolOpen(false); closeMenu("sub-sites"); }}>
                                            Sites
                                        </Link>
                                    </li>
                                    <li role="none">
                                        <Link role="menuitem" to="/solution/patients" onClick={() => { setSolOpen(false); closeMenu("sub-patients"); }}>
                                            Patients
                                        </Link>
                                    </li>
                                </ul>
                            </li>

                            {/* About */}
                            <li>
                                <Link to="/team" className={location.pathname === "/team" ? "active" : ""} onClick={() => closeMenu("nav-link")}>
                                    About Us
                                </Link>
                            </li>

                            {/* CTA */}
                            <li className="l-nav__cta-wrap">
                                <Link
                                    to="/contact#lummo-demo-form"
                                    className={`l-nav__cta ${location.pathname === "/contact" ? "active" : ""}`}
                                    onClick={() => closeMenu("cta")}
                                >
                                    Book a demo
                                </Link>
                            </li>
                        </ul>

                        {/* Hamburger — mobile only (hidden on desktop via CSS) */}
                        <div className="l-nav__right">
                            <button
                                className={`l-nav__toggle ${isMenuOpen ? "is-open" : ""}`}
                                onClick={toggleMenu}
                                aria-label="Toggle menu"
                                aria-expanded={isMenuOpen}
                                aria-controls="mobile-menu"
                            >
                <span className="l-nav__icon" aria-hidden="true">
                  <span className="l-nav__bar" />
                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* === PORTAL del submenú (solo desktop) === */}
            {solOpen && isDesktop &&
                createPortal(
                    <div
                        className="submenu-portal-container"
                        style={{
                            position: 'fixed', // Cambiado a fixed para que funcione con scroll
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none', // No interfiere con clics en el resto de la página
                            zIndex: 9999
                        }}
                    >
                        <ul
                            id="sol-submenu-portal"
                            className="l-nav__submenu--portal glass border-gradient"
                            role="menu"
                            aria-label="Solution"
                            style={{
                                position: 'absolute',
                                left: solPos.left,
                                top: solPos.top,
                                minWidth: solPos.minWidth,
                                display: 'block',
                                pointerEvents: 'auto', // Permitir interacción con el submenú
                                zIndex: 9999
                            }}
                        >
                            <li role="none">
                                <Link role="menuitem" to="/solution/cro" onClick={() => { closeSolution(); }}>
                                    CRO
                                </Link>
                            </li>
                            <li role="none">
                                <Link role="menuitem" to="/solution/sites" onClick={() => { closeSolution(); }}>
                                    Sites
                                </Link>
                            </li>
                            <li role="none">
                                <Link role="menuitem" to="/solution/patients" onClick={() => { closeSolution(); }}>
                                    Patients
                                </Link>
                            </li>
                        </ul>
                    </div>,
                    document.body
                )
            }

            {isMenuOpen && <div className="l-nav__overlay" onClick={() => closeMenu("overlay")} />}
        </nav>
    );
};

export default Navbar;
